import { useState, useRef, useEffect } from 'react'
import { NodeResizer } from '@xyflow/react'
import { Type } from 'lucide-react'

const COLORS = [
  { label: 'Yellow',  bg: 'rgba(234, 179, 8, 0.08)',   border: 'rgba(234, 179, 8, 0.4)',   text: '#fbbf24' },
  { label: 'Blue',    bg: 'rgba(59, 130, 246, 0.08)',  border: 'rgba(59, 130, 246, 0.4)',  text: '#60a5fa' },
  { label: 'Purple',  bg: 'rgba(139, 92, 246, 0.08)',  border: 'rgba(139, 92, 246, 0.4)',  text: '#a78bfa' },
  { label: 'Green',   bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.4)', text: '#34d399' },
  { label: 'Red',     bg: 'rgba(239, 68, 68, 0.08)',   border: 'rgba(239, 68, 68, 0.4)',   text: '#f87171' },
]

export default function AnnotationNode({ id, data, selected }) {
  const [text, setText] = useState(data?.text || 'Add a note...')
  const [colorIdx, setColorIdx] = useState(data?.colorIdx || 0)
  const [editing, setEditing] = useState(false)
  const textareaRef = useRef(null)
  const color = COLORS[colorIdx]

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [editing])

  const cycleColor = (e) => {
    e.stopPropagation()
    setColorIdx(i => (i + 1) % COLORS.length)
  }

  return (
    <>
      <NodeResizer
        isVisible={selected}
        minWidth={160}
        minHeight={80}
        handleStyle={{ background: color.text, width: 8, height: 8 }}
        lineStyle={{ borderColor: color.border }}
      />
      <div
        className="w-full h-full rounded-xl p-3 relative"
        style={{
          background: color.bg,
          border: `1.5px dashed ${color.border}`,
          minWidth: 160,
          minHeight: 80,
        }}
        onDoubleClick={() => setEditing(true)}
      >
        {/* Color picker */}
        <div className="absolute top-2 right-2 flex gap-1">
          {COLORS.map((c, i) => (
            <button
              key={i}
              onClick={() => setColorIdx(i)}
              className={`w-3 h-3 rounded-full transition-transform ${colorIdx === i ? 'scale-125' : 'opacity-50 hover:opacity-100'}`}
              style={{ background: c.text }}
            />
          ))}
        </div>

        {editing ? (
          <textarea
            ref={textareaRef}
            className="w-full h-full bg-transparent resize-none outline-none text-sm leading-relaxed"
            style={{ color: color.text, minHeight: 60 }}
            value={text === 'Add a note...' ? '' : text}
            placeholder="Add a note..."
            onChange={e => setText(e.target.value)}
            onBlur={() => { setEditing(false); if (!text.trim()) setText('Add a note...') }}
            onKeyDown={e => e.key === 'Escape' && setEditing(false)}
          />
        ) : (
          <p
            className="text-sm leading-relaxed cursor-text select-none whitespace-pre-wrap"
            style={{ color: color.text }}
          >
            {text}
          </p>
        )}

        {!editing && (
          <div className="absolute bottom-1.5 right-2 opacity-0 group-hover:opacity-100">
            <Type size={10} style={{ color: color.text }} />
          </div>
        )}
      </div>
    </>
  )
}
